import { useState } from 'react';
import { Settings } from 'lucide-react';
import Link from 'next/link';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import type { Me } from '../../../../lib/me';
import { Avatar, Button, ProgressBar } from '../../ui';
import { EarnCreditsOffer } from '../../earn-credits/EarnCreditsOffer';
import { BuyCreditsSheet } from '../../../settings/shared';
import { CommunityLink } from '../components/CommunityLink';
import { ProfileLinkMenuLink } from '../components/ProfileLinkMenuLink';
import { useAccountSummary } from '../hooks/use-account-summary';
import { useCreditsSummary } from '../hooks/use-credits-summary';
import { useCreditsWidgetState } from '../../../../lib/use-credits-widget-state';
import { CreditsMenuWidget } from '../../credits-widgets/CreditsMenuWidget';
import { useTranslation } from '../../../../lib/i18n';
import { usePersonalSaleActive } from '../../../../lib/use-personal-sale';
import styles from './MobileSidebar.module.css';

// The drawer's floor (Figma 8265:84671): the community link over the credits card over the account
// row, on a fade of the drawer's darkest step so the list scrolls away underneath.
//
// "Join Symphony community" is a child of THIS box, not of the scroll region above it, so it sits
// inside that fade the way the credits card does — Figma `Sidebar-Menu` 2679:3158 puts it at the top
// of the same bottom cluster on the wide rail. A row that can scroll out of reach is the thing the
// floor exists to prevent. Same component the rail renders, in its drawer skin.
export function MobileFooter({ me }: { me: Me | null }) {
  return (
    <div className={styles.footer}>
      <GenericEventsRouteProvider value="community">
        <CommunityLink variant="drawer" />
      </GenericEventsRouteProvider>
      <ProfileLinkMenuLink variant="drawer" />
      <GenericEventsRouteProvider value="credits">
        <CreditsCard />
      </GenericEventsRouteProvider>
      <GenericEventsRouteProvider value="account">
        <AccountRow me={me} />
      </GenericEventsRouteProvider>
    </div>
  );
}

// Plan name + its CTA, then the usage line over its bar — or, in the upsell arm, plan name over an
// upsell line with a full-width CTA at the foot (Figma 15375-105152).
//
// **The card BODY does what the CTA does** — the package picker, or the Buy-more-credits sheet on the
// top tier — same as the wide rail's card and both phones' drawer card (iOS `.onTapGesture` in
// `DrawerOverlay.swift`, Android `.clickable` in `Drawer.kt`).
//
// Body and CTA must NOT nest — `<Button href>` renders a real `<a>`, and an `<a>` inside an `<a>` is
// invalid HTML — so the body is a STRETCHED `<Link>` behind the content and the content is
// click-through (`pointer-events: none`) except for the controls, which opt back in. That is the same
// construction the wide rail's card already uses (`sidebar/components/AccountCard.tsx`), so the two web
// viewports share one idiom. A real anchor (on the picker arm) also keeps cmd-click / middle-click.
// The hit target is out of the tab order and hidden from assistive tech: the CTA is the same action,
// already focusable and labelled. On the top-up arm it is a bare <span>, not a button element — a mouse-only
// surface, not a control (see `button-spec.test.ts`).
function CreditsCard() {
  const { t } = useTranslation();
  const credits = useCreditsSummary();
  // The low/out-of-credits card replaces the ordinary drawer card on mobile-web too, so mobile-web
  // matches the native drawer (its `.orion-credits-card` tokens are theme-aware, so it renders correctly
  // on this permanently-dark island). Driven by the live balance being low/out.
  const widget = useCreditsWidgetState();
  // Personal sale: while the offer is live the credits CTA becomes "Upgrade and save 30%" and moves to a
  // full-width foot — same rule as the wide rail's AccountCard — replacing the old inline "30% OFF"
  // badge, which is gone. Read unconditionally (hooks rule), ahead of the early returns below.
  const saleActive = usePersonalSaleActive();
  const [buyOpen, setBuyOpen] = useState(false);
  if (widget.level !== 'none') return <CreditsMenuWidget />;
  if (!credits) return null;

  // The CTA is a full-width button at the FOOT of the card in every case, under the meter (Figma
  // 15375-105152: 243 at the card's content width, 30 tall = DS `small`). Accent, not the black primary
  // — Figma 15375-104637; the drawer is a permanently-dark island (`data-theme="dark"` on
  // MobileSidebar), the surface those frames draw.
  //
  // Two arms inside it — the top tier sells a top-up rather than a plan (see `CreditsSummary.upgrade`).
  // The `topup` arm carries no href on purpose: it opens a sheet, and a link would let a long-press
  // offer "open in new tab" onto the picker instead.
  // While the offer is live the CTA sells the discount ("Upgrade and save 30%"); otherwise the ordinary
  // Upgrade / Switch / Get more credits label. A personal-sale account is always Free (picker arm), but
  // the swap covers both arms for safety.
  const ctaLabel = saleActive ? t('personalSale.creditsCta') : credits.upgrade.label;
  const cta =
    credits.upgrade.kind === 'topup' ? (
      <Button
        color="accent"
        size="small"
        fullWidth
        onClick={() => {
          credits.upgrade.onClick();
          setBuyOpen(true);
        }}
      >
        {ctaLabel}
      </Button>
    ) : (
      <Button
        color="accent"
        size="small"
        fullWidth
        href={credits.upgrade.route}
        linkAs={credits.upgrade.path}
        onClick={credits.upgrade.onClick}
      >
        {ctaLabel}
      </Button>
    );

  return (
    <div className={styles.creditsCardShell}>
      {/* The body's hit target. Stretched behind the content rather than wrapped around it, so the CTA
          and the earn offer stay valid (and separately clickable) elements rather than nested links. */}
      {credits.upgrade.kind === 'topup' ? (
        <span
          aria-hidden
          title={credits.title}
          onClick={() => {
            credits.upgrade.onClick();
            setBuyOpen(true);
          }}
          className={styles.creditsCardLink}
        />
      ) : (
        <Link
          href={credits.upgrade.route}
          as={credits.upgrade.path}
          onClick={credits.upgrade.onClick}
          tabIndex={-1}
          aria-hidden
          title={credits.title}
          className={styles.creditsCardLink}
        />
      )}
      <div className={styles.creditsCard}>
        {/* Head: the plan name alone (the CTA has moved to the full-width foot in every case). The
          earn-credits offer (red band only, behind its OWN gate) stays here — a secondary control that
          never became the primary.

          The CTA is offered on every plan: neither native card gates it on free/paid either, and both
          hide only the whole card (on `creditsCap <= 0`, our "no meter" case). `upgrade.label` is what
          says Upgrade / Switch / Get more credits. Parity on the PICKER arm is the GATE, not the
          destination — native picks between the Symphony picker and the Wixel page itself (behind a
          confirmation), where web always opens /packages and that page decides. */}
        <div className={styles.creditsHead}>
          <span className={styles.planLabel}>{credits.planLabel}</span>
          <span className={styles.creditsActions}>
            <EarnCreditsOffer entry={{ kind: 'sidebar_credits', fraction: credits.fraction }} pageName="menu" variant="mobile" size="tiny" />
          </span>
        </div>
        {/* The usage line over its meter, kept in every case — the "{used} of {cap}" line is the
          ProgressBar's own label above the track — then the full-width lime CTA at the foot. */}
        <ProgressBar progress={credits.progress} value={credits.fraction * 100} label={credits.usageLabel} decorative />
        <span className={styles.creditsFootCta}>{cta}</span>
      </div>
      {/* The top tier's top-up opens the same Buy-more-credits SHEET the mobile account card
          uses — a phone gets the sheet, not the desktop modal.

          OUTSIDE the click-through body on purpose: the sheet is the CTA's own surface, and a
          `pointer-events: none` ancestor would reach its content and leave every control in it dead. */}
      {credits.buyCredits && (
        <BuyCreditsSheet
          open={buyOpen}
          onClose={() => setBuyOpen(false)}
          topup={credits.buyCredits.topup}
          planName={credits.buyCredits.planName}
          checkoutHref={credits.buyCredits.checkoutHref}
        />
      )}
    </div>
  );
}

// The whole row opens Settings (and carries the hidden brand-toggle long press),
// with the design's settings gear as the trailing affordance.
function AccountRow({ me }: { me: Me | null }) {
  const { initials, displayName, photoUrl, onPhotoError, settings } = useAccountSummary(me);
  // Reserve the row's height before `me` resolves so the footer doesn't jump.
  if (!initials) return <div style={{ minHeight: 36 }} />;

  return (
    <Link
      href={settings.route}
      as={settings.path}
      onClick={settings.onClick}
      aria-label={settings.label}
      title={displayName}
      className={styles.accountRow}
    >
      <span className={styles.accountButton}>
        <Avatar initials={initials} photoUrl={photoUrl} onPhotoError={onPhotoError} size={30} />
        <span className={styles.accountName}>{displayName}</span>
      </span>
      <span className={styles.accountGear} aria-hidden>
        <Settings size={16} />
      </span>
    </Link>
  );
}
